/**
 * Pure formatting + validation helpers for the Add Payment Account modal (CNP-133).
 * No React, no i18n — just input→output so they're trivially unit-testable. Bounds match
 * the legacy mdvipconnect product (card min-13, account 8-17, expiry not-past / ≤ +12yr).
 */
import { endOfMonth, isBefore } from 'date-fns';
import type { CardNetwork } from './paymentAccount.types';

/** Longest supported PAN: 16 (all networks except Amex, which is 15). Display caps here. */
const MAX_PAN_DIGITS = 16;

/** Strip non-digits, cap length, and group for display. Amex (IIN 3) uses 4-6-5; others, blocks of 4. */
export function formatCardNumber(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, MAX_PAN_DIGITS);
  if (digits[0] === '3') {
    const amex = digits.slice(0, 15);
    return [amex.slice(0, 4), amex.slice(4, 10), amex.slice(10, 15)].filter(Boolean).join(' ');
  }
  return (digits.match(/.{1,4}/g) ?? []).join(' ');
}

/** Mask a raw expiry entry into "MM/YY" as the user types. */
export function formatExpiry(value: string): string {
  const digits = value.replace(/\D/g, '');
  // Insert the slash only once a year digit exists. Forcing it at exactly two digits traps
  // backspace (deleting the slash re-adds it), so wait for the third digit to add "MM/".
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}`;
}

/** Card network from the leading digits (sufficient for display + CVV-length rules). */
export function detectNetwork(cardNumber: string): CardNetwork {
  const digits = cardNumber.replace(/\D/g, '');
  switch (digits[0]) {
    case '4':
      return 'visa';
    case '5':
      return 'mastercard';
    case '3':
      return 'amex';
    case '6':
      return 'discover';
  }
  // 2-series Mastercard (BIN 2221–2720), once enough digits are present to decide.
  const firstFour = Number(digits.slice(0, 4));
  if (firstFour >= 2221 && firstFour <= 2720) return 'mastercard';
  return 'unknown';
}

/** Amex CVVs are 4 digits; every other network is 3. */
export function expectedCvvLength(network: CardNetwork): number {
  return network === 'amex' ? 4 : 3;
}

/** Amex PANs are 15 digits; every other network is 16 (per finance). */
export function expectedCardLength(network: CardNetwork): number {
  return network === 'amex' ? 15 : 16;
}

/**
 * Parse a well-formed "MM/YY" into numeric month + 4-digit year. The century is derived from
 * the clock rather than hardcoded (so it survives the 2100 rollover); cards are always-future,
 * so the current century is the correct base.
 */
export function parseExpiry(expiry: string, now: Date = new Date()): { month: number; year: number } {
  const [month, year] = expiry.split('/');
  const century = Math.floor(now.getFullYear() / 100) * 100;
  return { month: Number(month), year: century + Number(year) };
}

/** A card is usable through the end of its expiry month. */
export function isNotPast(expiry: string, now: Date = new Date()): boolean {
  const { month, year } = parseExpiry(expiry, now);
  const lastDayOfExpiryMonth = endOfMonth(new Date(year, month - 1, 1));
  return !isBefore(lastDayOfExpiryMonth, now);
}

/** Reject expiries more than 12 years out (legacy DV-6888 bound). */
export function isNotTooFarFuture(expiry: string, now: Date = new Date()): boolean {
  const { year } = parseExpiry(expiry, now);
  return year <= now.getFullYear() + 12;
}
